"use client";

import { useEffect, useState } from "react";
import { Bell, BellRing, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { createClient } from "@/lib/supabase/client";
import { formatCurrency, isPenceCurrency, descalePence } from "@/lib/format";
import { AccountGateButton } from "@/components/auth/account-gate-button";

interface AlertRow {
  threshold_score: number | null;
  notify_pe_below_median: boolean;
  notify_new_filing: boolean;
  notify_earnings_today: boolean;
  notify_dividend_ex: boolean;
  notify_pe_below_own_median: boolean;
  notify_earnings_reminder: boolean;
  earnings_reminder_days_before: number;
  notify_target_price: boolean;
  target_price: number | null;
  notify_fair_value_below: boolean;
  notify_peer_divergence: boolean;
  notify_dividend_pay: boolean;
  notify_score_trend: boolean;
  notify_consensus_change: boolean;
}

const EMPTY_EVENTS = {
  notify_pe_below_median: false,
  notify_new_filing: false,
  notify_earnings_today: false,
  notify_dividend_ex: false,
  notify_pe_below_own_median: false,
  notify_earnings_reminder: false,
  notify_target_price: false,
  notify_fair_value_below: false,
  notify_peer_divergence: false,
  notify_dividend_pay: false,
  notify_score_trend: false,
  notify_consensus_change: false,
};
const DEFAULT_EARNINGS_REMINDER_DAYS = 3;

/**
 * Score-Schwellen- und Ereignis-Benachrichtigung je Symbol (siehe backlog.md ->
 * "Feedback-Runde 4" Punkt 4 "Event-Trigger-Erweiterung"). Nutzt direkt den
 * RLS-geschuetzten Browser-Client (gleiches Muster wie WatchlistStarButton) statt
 * einer eigenen API-Route - die taegliche Pruefung/den Versand uebernimmt
 * netlify/functions/score-alerts-check.mts separat. Nur fuer eingeloggte Nutzer
 * sichtbar (braucht eine E-Mail-Adresse zum Benachrichtigen).
 *
 * `currentPrice`/`currency` (backlog.md -> C13, Ziel-Kaufkurs-Alarm) sind optional und
 * dienen nur als Anzeigehilfe/Vorbelegung fuer die Kursmarke - der Aufrufer hat
 * `ScoreSummary` ohnehin schon geladen, kein Zusatz-Request noetig.
 *
 * UI auf 5 Typen reduziert (backlog.md -> [Punkt 23], 2026-08-12): Score-Schwelle,
 * Kurs-Marke, neuer Quartalsbericht (notify_earnings_today, umbenannt), Dividenden-
 * Ex-Tag, Dividenden-Zahltag (neu, notify_dividend_pay). **2 neue, bewusst wieder
 * sichtbare Typen ergaenzt** (backlog.md -> "Weiterer Nutzwert-Katalog" -> Score/
 * Analyse, 2026-08-19): Score-Trend (notify_score_trend) und Kurszielkonsens-Aenderung
 * (notify_consensus_change) - anders als die unten weiterhin ausgeblendeten Typen sind
 * das zwei von Erik selbst konkret gewuenschte NEUE Alarme, keine Wiederherstellung der
 * bewusst deprioritisierten alten 6. Die uebrigen, weiterhin nicht anklickbaren Typen
 * (notify_pe_below_median/_own_median, notify_fair_value_below,
 * notify_peer_divergence, notify_new_filing, notify_earnings_reminder) bleiben bewusst
 * weiterhin Teil von `AlertRow`/`events`/dem `handleSave()`-Upsert-Spread - reine
 * UI-Reduzierung ohne Backend-Aenderung fuer diese Typen (siehe STATUS.md): bereits
 * bestehende Konfigurationen fuer diese Typen laufen unveraendert weiter (Supabase-
 * `.upsert()` laesst nicht mitgesendete Spalten unangetastet), nur neu einrichten kann
 * man sie ueber diese UI nicht mehr - `isActive` beruecksichtigt deshalb weiterhin ALLE
 * Typen, nicht nur die 5 sichtbaren, sonst wuerde die Glocke bei jemandem mit
 * ausschliesslich einem der 6 ausgeblendeten Typen faelschlich "inaktiv" zeigen.
 */
export function ScoreAlertButton({
  symbol,
  currentPrice,
  currency = "USD",
}: {
  symbol: string;
  currentPrice?: number;
  currency?: string;
}) {
  // Pence-notierte LSE-Aktien (GBp/GBX) liefern currentPrice roh in Pence (siehe
  // descalePence()-Kommentar in lib/format.ts) - fuer die reine Anzeige/Voreingabe hier
  // deskaliert, beim Speichern unten wieder zurueckskaliert (handleSave), damit der
  // gespeicherte target_price weiterhin direkt mit dem ebenso rohen currentPrice
  // vergleichbar bleibt, den score-alerts-check.mts taeglich laedt.
  const isPence = isPenceCurrency(currency);
  const displayCurrency = isPence ? "GBP" : currency;
  const displayCurrentPrice = currentPrice !== undefined ? descalePence(currentPrice, currency) : undefined;
  const [userId, setUserId] = useState<string | null | undefined>(undefined);
  const [threshold, setThreshold] = useState<number | null>(null);
  const [draft, setDraft] = useState("80");
  const [useThreshold, setUseThreshold] = useState(false);
  const [events, setEvents] = useState(EMPTY_EVENTS);
  const [earningsReminderDays, setEarningsReminderDays] = useState(DEFAULT_EARNINGS_REMINDER_DAYS);
  const [targetPriceDraft, setTargetPriceDraft] = useState(
    displayCurrentPrice !== undefined ? displayCurrentPrice.toFixed(2) : "",
  );
  const [isPending, setIsPending] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const supabase = createClient();
    let cancelled = false;

    supabase.auth.getUser().then(({ data }) => {
      if (!cancelled) setUserId(data.user?.id ?? null);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    const supabase = createClient();
    supabase
      .from("score_alerts")
      .select(
        "threshold_score, notify_pe_below_median, notify_new_filing, notify_earnings_today, notify_dividend_ex, notify_pe_below_own_median, notify_earnings_reminder, earnings_reminder_days_before, notify_target_price, target_price, notify_fair_value_below, notify_peer_divergence, notify_dividend_pay, notify_score_trend, notify_consensus_change",
      )
      .eq("symbol", symbol)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        const row = data as AlertRow | null;
        if (row) {
          setThreshold(row.threshold_score);
          setUseThreshold(row.threshold_score !== null);
          if (row.threshold_score !== null) setDraft(String(row.threshold_score));
          setEvents({
            notify_pe_below_median: row.notify_pe_below_median,
            notify_new_filing: row.notify_new_filing,
            notify_earnings_today: row.notify_earnings_today,
            notify_dividend_ex: row.notify_dividend_ex,
            notify_pe_below_own_median: row.notify_pe_below_own_median,
            notify_earnings_reminder: row.notify_earnings_reminder,
            notify_target_price: row.notify_target_price,
            notify_fair_value_below: row.notify_fair_value_below,
            notify_peer_divergence: row.notify_peer_divergence,
            notify_dividend_pay: row.notify_dividend_pay,
            notify_score_trend: row.notify_score_trend,
            notify_consensus_change: row.notify_consensus_change,
          });
          if (row.earnings_reminder_days_before) setEarningsReminderDays(row.earnings_reminder_days_before);
          if (row.target_price !== null) {
            setTargetPriceDraft(String(isPence ? descalePence(row.target_price, currency) : row.target_price));
          }
        }
      });
    return () => {
      cancelled = true;
    };
  }, [userId, symbol, currency, isPence]);

  // Auth-Status noch nicht geladen: noch nichts rendern.
  if (userId === undefined) return null;

  // Gast (Erik 2026-07-17: "Alerts aktivieren" verlangt ein kostenloses Konto) - Button
  // bleibt sichtbar, oeffnet aber das Signup-Gate statt der Einrichtung.
  if (userId === null) {
    return (
      <AccountGateButton
        label="Anmelden, um Benachrichtigungen einzurichten"
        reason={`Erstelle ein kostenloses Konto, um Benachrichtigungen für ${symbol} einzurichten.`}
      >
        <Lock className="size-4" />
      </AccountGateButton>
    );
  }

  const isActive =
    threshold !== null ||
    events.notify_pe_below_median ||
    events.notify_new_filing ||
    events.notify_earnings_today ||
    events.notify_dividend_ex ||
    events.notify_pe_below_own_median ||
    events.notify_earnings_reminder ||
    events.notify_target_price ||
    events.notify_fair_value_below ||
    events.notify_peer_divergence ||
    events.notify_dividend_pay ||
    events.notify_score_trend ||
    events.notify_consensus_change;

  async function handleSave() {
    const value = useThreshold ? Math.round(Number(draft)) : null;
    if (useThreshold && (!Number.isFinite(value) || value! < 0 || value! > 100)) return;
    if (value === null && !Object.values(events).some(Boolean)) return; // mindestens ein Trigger noetig
    if (events.notify_earnings_reminder && (earningsReminderDays < 1 || earningsReminderDays > 30)) return;
    const targetPriceValue = events.notify_target_price ? Number(targetPriceDraft) : null;
    if (events.notify_target_price && (!Number.isFinite(targetPriceValue) || targetPriceValue! <= 0)) return;
    // Zurueck auf dieselbe rohe Skala wie currentPrice (siehe Kommentar oben) - so
    // bleibt der gespeicherte target_price direkt vergleichbar mit dem rohen
    // currentPrice, den score-alerts-check.mts taeglich neu laedt.
    const storedTargetPrice = targetPriceValue !== null && isPence ? targetPriceValue * 100 : targetPriceValue;

    setIsPending(true);
    const supabase = createClient();
    const { error } = await supabase.from("score_alerts").upsert(
      {
        user_id: userId,
        symbol,
        threshold_score: value,
        triggered_at: null,
        ...events,
        earnings_reminder_days_before: earningsReminderDays,
        target_price: storedTargetPrice,
      },
      { onConflict: "user_id,symbol" },
    );
    if (!error) {
      setThreshold(value);
      setOpen(false);
    }
    setIsPending(false);
  }

  async function handleRemove() {
    setIsPending(true);
    const supabase = createClient();
    const { error } = await supabase.from("score_alerts").delete().eq("symbol", symbol).eq("user_id", userId);
    if (!error) {
      setThreshold(null);
      setUseThreshold(false);
      setEvents(EMPTY_EVENTS);
      setEarningsReminderDays(DEFAULT_EARNINGS_REMINDER_DAYS);
      setTargetPriceDraft(displayCurrentPrice !== undefined ? displayCurrentPrice.toFixed(2) : "");
      setOpen(false);
    }
    setIsPending(false);
  }

  const label = isActive ? "Benachrichtigung aktiv" : "Benachrichtigung einrichten";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="icon" aria-label={label} title={label}>
          {isActive ? <BellRing className="size-4 text-gold-text" /> : <Bell className="size-4" />}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72">
        <p className="text-xs text-muted-foreground">
          Benachrichtige mich per E-Mail bei diesen Ereignissen für {symbol}.
        </p>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={useThreshold}
            onChange={(e) => setUseThreshold(e.target.checked)}
          />
          Score erreicht Wert
        </label>
        {useThreshold && (
          <Input
            type="number"
            min={0}
            max={100}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            aria-label="Score-Schwelle"
            className="bg-card"
          />
        )}

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={events.notify_target_price}
            onChange={(e) => setEvents((prev) => ({ ...prev, notify_target_price: e.target.checked }))}
          />
          Kurs fällt unter Marke
        </label>
        {events.notify_target_price && (
          <>
            <Input
              type="number"
              min={0}
              step="0.01"
              value={targetPriceDraft}
              onChange={(e) => setTargetPriceDraft(e.target.value)}
              aria-label="Ziel-Kaufkurs"
              className="bg-card"
            />
            {displayCurrentPrice !== undefined && (
              <p className="text-xs text-muted-foreground">
                Aktueller Kurs: {formatCurrency(displayCurrentPrice, displayCurrency)}
              </p>
            )}
          </>
        )}

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={events.notify_earnings_today}
            onChange={(e) => setEvents((prev) => ({ ...prev, notify_earnings_today: e.target.checked }))}
          />
          Neuer Quartalsbericht
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={events.notify_dividend_ex}
            onChange={(e) => setEvents((prev) => ({ ...prev, notify_dividend_ex: e.target.checked }))}
          />
          Dividenden-Ex-Tag
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={events.notify_dividend_pay}
            onChange={(e) => setEvents((prev) => ({ ...prev, notify_dividend_pay: e.target.checked }))}
          />
          Dividenden-Zahltag
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={events.notify_score_trend}
            onChange={(e) => setEvents((prev) => ({ ...prev, notify_score_trend: e.target.checked }))}
          />
          Score sinkt spürbar vom Höchststand
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={events.notify_consensus_change}
            onChange={(e) => setEvents((prev) => ({ ...prev, notify_consensus_change: e.target.checked }))}
          />
          Analysten-Kursziel ändert sich spürbar
        </label>

        <p className="text-xs text-muted-foreground">
          Ausgelöste Alarme werden gebündelt in maximal einer E-Mail pro Woche verschickt, nie pro Ereignis einzeln.
        </p>

        <div className="flex gap-2 pt-1">
          <Button type="button" size="sm" onClick={handleSave} disabled={isPending}>
            Speichern
          </Button>
          {isActive && (
            <Button type="button" size="sm" variant="ghost" onClick={handleRemove} disabled={isPending}>
              Entfernen
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
